import { useCallback, useEffect, useRef, useState } from 'react';
import { WEATHER_LAT, WEATHER_LON } from '../config/app';
import { weatherCodeToLabelPl, windDirectionToLabel } from '../utils/weatherFormat';

const OPEN_METEO = 'https://api.open-meteo.com/v1/forecast';

const DEFAULT_POLL_MS = 5 * 60 * 1000;

function parsePollMs() {
    const n = Number(process.env.REACT_APP_WEATHER_POLL_MS);
    return Number.isFinite(n) && n >= 60_000 ? n : DEFAULT_POLL_MS;
}

export function useWeather() {
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const mounted = useRef(true);
    const pollMs = parsePollMs();

    const fetchWeather = useCallback(async () => {
        try {
            const url = new URL(OPEN_METEO);
            url.searchParams.set('latitude', String(WEATHER_LAT));
            url.searchParams.set('longitude', String(WEATHER_LON));
            url.searchParams.set(
                'current',
                [
                    'temperature_2m',
                    'relative_humidity_2m',
                    'weather_code',
                    'wind_speed_10m',
                    'wind_direction_10m',
                    'wind_gusts_10m',
                    'precipitation',
                ].join(',')
            );
            url.searchParams.set('timezone', 'Europe/Warsaw');
            url.searchParams.set('wind_speed_unit', 'kmh');

            const res = await fetch(url.toString(), { cache: 'no-store' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            const c = json.current;
            if (!c) throw new Error('Brak danych');

            const next = {
                tempC: Math.round(c.temperature_2m),
                humidityPct: Math.round(c.relative_humidity_2m),
                weatherCode: c.weather_code,
                labelPl: weatherCodeToLabelPl(c.weather_code),
                windKmh: c.wind_speed_10m != null ? Math.round(c.wind_speed_10m) : null,
                windDir: windDirectionToLabel(c.wind_direction_10m),
                windGustKmh: c.wind_gusts_10m != null ? Math.round(c.wind_gusts_10m) : null,
                precipitationMm: c.precipitation != null ? Math.round(c.precipitation * 10) / 10 : null,
                updatedAt: new Date(),
            };

            if (mounted.current) {
                setSnapshot(next);
                setError(null);
            }
        } catch (e) {
            if (mounted.current) setError(e.message || 'Błąd');
        } finally {
            if (mounted.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        fetchWeather();
        const id = setInterval(fetchWeather, pollMs);
        const onVis = () => {
            if (document.visibilityState === 'visible') fetchWeather();
        };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            mounted.current = false;
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [fetchWeather, pollMs]);

    return { snapshot, loading, error, refetch: fetchWeather };
}
